import React, { useEffect, useMemo, useState } from "react";
import ToolShell from "@/components/ToolShell";
import CurrencyInput from "@/components/CurrencyInput";
import { amortization, LOAN_TYPES } from "@/lib/loans";
import { track } from "@/lib/analytics";
import { CreditCard } from "lucide-react";

export default function LoansPage() {
  const [principal, setPrincipal] = useState("27000");
  const [rate, setRate] = useState(6.53);
  const [years, setYears] = useState(10);

  useEffect(() => { track.tool_opened("loans"); }, []);

  const result = useMemo(
    () => amortization({ principal: Number(principal) || 0, annualRate: rate, years }),
    [principal, rate, years]
  );

  return (
    <ToolShell tool="Tool 08 · Loan Reality Check" title="What your loan will actually cost." subtitle="All math runs in your browser. No login, no saved state. Play with the sliders to see how rate and term change the monthly payment and total interest.">
      <div className="card mb-6 grid sm:grid-cols-3 gap-5" data-testid="loan-inputs">
        <CurrencyInput
          id="loan-principal"
          label="Loan amount"
          value={principal}
          onChange={(v) => { setPrincipal(v); track.loan_calculated(); }}
          placeholder="0"
          testid="loan-principal"
        />
        <div>
          <label className="block text-sm text-ink-muted mb-2">Interest rate (%)</label>
          <input type="number" step="0.01" className="input" value={rate}
            onChange={(e) => { setRate(Number(e.target.value) || 0); track.loan_calculated(); }}
            data-testid="loan-rate" />
        </div>
        <div>
          <label className="block text-sm text-ink-muted mb-2">Term (years)</label>
          <input type="number" className="input" value={years}
            onChange={(e) => { setYears(Number(e.target.value) || 0); track.loan_calculated(); }}
            data-testid="loan-years" />
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-8" data-testid="loan-result">
        <Stat label="Monthly payment" value={`$${result.monthly.toLocaleString()}`} big />
        <Stat label="Total paid" value={`$${result.totalPaid.toLocaleString()}`} />
        <Stat label="Interest cost" value={`$${result.totalInterest.toLocaleString()}`} color="text-amber" />
        <Stat label="Income you'll need" value={`$${result.requiredIncome.toLocaleString()}/yr`} sub="Rule of thumb: loan ≤ 10% of gross" />
      </div>

      <div className="card" data-testid="loan-types">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-accent" />
          <h2 className="font-heading text-2xl text-ink">Common federal loan rates (2025-26)</h2>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-dim border-b border-border">
              <th className="py-3 pr-4">Loan</th>
              <th className="py-3 pr-4">Rate</th>
              <th className="py-3">Notes</th>
            </tr>
          </thead>
          <tbody>
            {LOAN_TYPES.map(l => (
              <tr key={l.id} className="border-b border-border last:border-0">
                <td className="py-3 pr-4 text-ink">{l.name}</td>
                <td className="py-3 pr-4 text-ink-muted">{l.rate.toFixed(2)}%</td>
                <td className="py-3 text-ink-muted text-xs">{l.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ToolShell>
  );
}

function Stat({ label, value, sub, color = "text-ink", big = false }) {
  return (
    <div className="card-tight card">
      <div className="text-xs uppercase tracking-widest text-ink-dim">{label}</div>
      <div className={`font-heading ${big ? "text-4xl" : "text-3xl"} ${color} mt-1`}>{value}</div>
      {sub && <div className="text-xs text-ink-dim mt-1">{sub}</div>}
    </div>
  );
}
